// Mark This Answer — the evaluative companion to source-examination. The learner
// reads sample answers and grades each against a rubric (yes / partial / no),
// then checks their evaluation against the examiner key and earns bonus marks.
// Ported from the reference revision app's "Mark This Answer" bonus section.
//
// Capture: one learning_interactions row per sample answer (activity_key = the
// answer id), system-actionable — the rubric judgements are graded against the
// authored key. See REVISION_TEMPLATE_PLAN.md §3.

import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { MarkThisAnswerSlideData, MarkThisAnswerSampleAnswer, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { getModuleProgress, MODULE_UNLOCK_PCT } from '../../../../../lib/revision-builder/moduleProgress';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: MarkThisAnswerSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: MarkThisAnswerSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}

const html = (s: string) => ({ dangerouslySetInnerHTML: { __html: s } });
type Verdict = 'yes' | 'partial' | 'no';
const VERDICTS: Verdict[] = ['yes', 'partial', 'no'];
const VERDICT_LABEL: Record<Verdict, string> = { yes: 'Yes', partial: 'Partial', no: 'No' };

export function MarkThisAnswerSlide({ data, theme, isEditing, onUpdate, onActivityResponse }: Props) {
  if (isEditing) return <MarkThisAnswerEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return <MarkThisAnswerStudent data={data} theme={theme} onActivityResponse={onActivityResponse} />;
}

function MarkThisAnswerStudent({
  data,
  theme,
  onActivityResponse,
}: {
  data: MarkThisAnswerSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // selections[answerId][criterionId] = verdict; checked[answerId] = true
  const [selections, setSelections] = useState<Record<string, Record<string, Verdict>>>({});
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const totalCriteria = useMemo(() => data.answers.reduce((n, a) => n + a.criteria.length, 0), [data.answers]);
  const earned = useMemo(() => {
    let n = 0;
    for (const a of data.answers) {
      if (!checked[a.id]) continue;
      for (const c of a.criteria) if (selections[a.id]?.[c.id] === c.correct) n++;
    }
    return n;
  }, [data.answers, checked, selections]);

  const pick = (answerId: string, criterionId: string, v: Verdict) => {
    if (checked[answerId]) return;
    setSelections((prev) => ({ ...prev, [answerId]: { ...prev[answerId], [criterionId]: v } }));
  };

  const allRated = (a: MarkThisAnswerSampleAnswer) =>
    a.criteria.every((c) => selections[a.id]?.[c.id] != null);

  const check = useCallback(
    (a: MarkThisAnswerSampleAnswer) => {
      const rated = a.criteria.every((c) => selections[a.id]?.[c.id] != null);
      if (!rated || checked[a.id]) return;
      const correctCount = a.criteria.filter((c) => selections[a.id]?.[c.id] === c.correct).length;
      setChecked((prev) => ({ ...prev, [a.id]: true }));
      onActivityResponse?.({
        activityType: 'mark-this-answer',
        mode: 'quiz',
        score: a.criteria.length ? correctCount / a.criteria.length : 0,
        responseData: {
          answerId: a.id,
          judgements: selections[a.id] ?? {},
          correctCount,
          total: a.criteria.length,
        },
      });
    },
    [selections, checked, onActivityResponse],
  );

  const allChecked = data.answers.length > 0 && data.answers.every((a) => checked[a.id]);

  // Module gate — when this MTA belongs to a revision module, the bonus is
  // locked until the learner has scored ≥70% across the module's source slides.
  const moduleProgress = data.moduleId ? getModuleProgress(data.moduleId) : null;
  const locked = !!moduleProgress && moduleProgress.total > 0 && moduleProgress.pct < MODULE_UNLOCK_PCT;

  if (locked && moduleProgress) {
    return (
      <div
        className="h-full w-full overflow-y-auto"
        style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
      >
        <div className="mx-auto max-w-[640px] px-6 py-10 grid place-items-center min-h-full">
          <div className="rounded-2xl p-8 text-center shadow-lg w-full" style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.textMuted}22` }}>
            <div className="text-3xl mb-2" aria-hidden>🔒</div>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Bonus · Mark This Answer</span>
            <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginTop: 6 }}>Locked</h1>
            <p className="text-sm mt-2 mb-5" style={{ color: theme.colors.textMuted }}>
              Score <strong style={{ color: theme.colors.text }}>{MODULE_UNLOCK_PCT}%</strong> across this module's sources to unlock the examiner-marking bonus.
            </p>
            <div className="flex items-center justify-between text-xs mb-1" style={{ color: theme.colors.text }}>
              <span>Module so far</span>
              <span style={{ color: theme.colors.textMuted }}>{moduleProgress.earned}/{moduleProgress.total} · {moduleProgress.pct}%</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: `${theme.colors.textMuted}22` }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${moduleProgress.pct}%`, background: accent }} />
            </div>
            <p className="text-xs mt-4" style={{ color: theme.colors.textMuted }}>
              Go back and lift your score on the source questions, then return here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="mb-5">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Mark this answer</span>
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginTop: 4 }}>{data.title}</h1>
          {data.subtitle && <p className="text-sm mt-1" style={{ color: theme.colors.textMuted }}>{data.subtitle}</p>}
          {moduleProgress && moduleProgress.total > 0 && (
            <p className="text-xs mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: `${accent}12`, color: accent }}>
              ★ Bonus unlocked — module score {moduleProgress.earned}/{moduleProgress.total} ({moduleProgress.pct}%)
            </p>
          )}
        </header>

        {/* The task being evaluated */}
        <div className="rounded-lg p-4 mb-5 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>The question</p>
          <div className="text-sm" style={{ color: theme.colors.text }} {...html(data.task)} />
          {data.rubricNote && <p className="text-xs italic mt-2" style={{ color: theme.colors.textMuted }} {...html(data.rubricNote)} />}
        </div>

        {data.answers.map((a) => {
          const isChecked = !!checked[a.id];
          const correctCount = a.criteria.filter((c) => selections[a.id]?.[c.id] === c.correct).length;
          return (
            <div key={a.id} className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold" style={{ color: theme.colors.text }}>{a.label}</span>
                <span className="text-xs font-semibold" style={{ color: accent }}>Evaluate this answer</span>
              </div>
              <div className="rounded-lg p-4 mb-3 text-sm leading-relaxed" style={{ background: theme.colors.surface, border: `1px solid ${theme.colors.textMuted}22`, color: theme.colors.text }} {...html(a.text)} />

              {a.criteria.map((c) => {
                const sel = selections[a.id]?.[c.id];
                const isRight = sel === c.correct;
                return (
                  <div key={c.id} className="mb-3">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm flex-1" style={{ color: theme.colors.text }}>{c.prompt}</span>
                      <div className="flex gap-1.5 shrink-0">
                        {VERDICTS.map((v) => {
                          const active = sel === v;
                          return (
                            <button
                              key={v}
                              onClick={() => pick(a.id, c.id, v)}
                              disabled={isChecked}
                              className="px-2.5 py-1 rounded-md text-xs font-semibold border"
                              style={{
                                background: active ? `${accent}1a` : 'transparent',
                                borderColor: active ? accent : `${theme.colors.textMuted}33`,
                                color: active ? accent : theme.colors.textMuted,
                                cursor: isChecked ? 'default' : 'pointer',
                              }}
                            >
                              {VERDICT_LABEL[v]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {isChecked && (
                      <div
                        className="mt-2 rounded-md p-2.5 text-xs leading-relaxed"
                        style={{
                          background: isRight ? feedbackStyle(theme, 'correct').bg : feedbackStyle(theme, 'incorrect').bg,
                          color: theme.colors.text,
                        }}
                      >
                        <span className="font-bold" style={{ color: isRight ? feedbackStyle(theme, 'correct').text : feedbackStyle(theme, 'incorrect').text }}>
                          {isRight ? '✓ Correct' : `✗ Examiner: ${VERDICT_LABEL[c.correct]}`}
                        </span>{' '}
                        <span {...html(c.explanation)} />
                      </div>
                    )}
                  </div>
                );
              })}

              {!isChecked ? (
                <div className="text-right">
                  <button
                    onClick={() => check(a)}
                    disabled={!allRated(a)}
                    className="px-5 py-2 rounded-lg font-semibold text-sm text-white"
                    style={{ background: accent, opacity: allRated(a) ? 1 : 0.4, cursor: allRated(a) ? 'pointer' : 'not-allowed' }}
                  >
                    Check my evaluation
                  </button>
                </div>
              ) : (
                <AnimatePresence>
                  <Reveal variant="up">
                    <div className="rounded-lg p-3 text-sm" style={{ background: `${accent}12`, border: `1px solid ${accent}40` }}>
                      <span className="font-bold" style={{ color: accent }}>{correctCount}/{a.criteria.length} correct</span>
                      {a.approxBand && (
                        <span style={{ color: theme.colors.textMuted }}> — this answer would score about <strong style={{ color: theme.colors.text }}>{a.approxBand}</strong>.</span>
                      )}
                    </div>
                  </Reveal>
                </AnimatePresence>
              )}
            </div>
          );
        })}

        {allChecked && (
          <div className="mt-2">
            <div className="rounded-lg p-3 mb-4 text-center text-sm font-semibold" style={{ background: `${accent}12`, color: accent }}>
              {earned} of {totalCriteria} examiner judgements matched
            </div>
            <ExperienceClose
              conclusion={data.conclusion}
              theme={theme}
              values={{ earned, total: totalCriteria }}
              fallback={`You matched the examiner on ${earned} of ${totalCriteria} judgements. Marking trains the same eye that lifts your own answers.`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Editor (minimal — aiOnly) ---------------------------------------------

function MarkThisAnswerEditor({
  data,
  theme,
  onUpdate,
}: {
  data: MarkThisAnswerSlideData;
  theme: Theme;
  onUpdate: (data: MarkThisAnswerSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = { background: theme.colors.background, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.text } as const;
  const patchAnswer = (id: string, patch: Partial<MarkThisAnswerSampleAnswer>) =>
    onUpdate({ ...data, answers: data.answers.map((a) => (a.id === id ? { ...a, ...patch } : a)) });

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input value={data.title} onChange={(e) => onUpdate({ ...data, title: e.target.value })} placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }} />
        <textarea value={data.task} onChange={(e) => onUpdate({ ...data, task: e.target.value })} placeholder="The question the answers respond to" rows={2}
          className="w-full px-3 py-2 rounded border text-sm" style={inputStyle} />
        {data.answers.map((a) => (
          <div key={a.id} className="rounded-lg border p-4 space-y-2" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
            <div className="flex items-center justify-between">
              <input value={a.label} onChange={(e) => patchAnswer(a.id, { label: e.target.value })} placeholder="Answer label"
                className="px-2 py-1 rounded border text-sm font-semibold" style={inputStyle} />
              <button onClick={() => onUpdate({ ...data, answers: data.answers.filter((x) => x.id !== a.id) })} className="text-xs text-gray-400 hover:text-red-500">×</button>
            </div>
            <textarea value={a.text} onChange={(e) => patchAnswer(a.id, { text: e.target.value })} placeholder="Sample answer text" rows={4}
              className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
            <input value={a.approxBand ?? ''} onChange={(e) => patchAnswer(a.id, { approxBand: e.target.value })} placeholder="Approx band (e.g. 4–5 (L2))"
              className="w-full px-2 py-1 rounded border text-sm" style={inputStyle} />
            {a.criteria.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <input value={c.prompt} onChange={(e) => patchAnswer(a.id, { criteria: a.criteria.map((x) => (x.id === c.id ? { ...x, prompt: e.target.value } : x)) })}
                  placeholder="Criterion" className="flex-1 px-2 py-1 rounded border text-sm" style={inputStyle} />
                <select value={c.correct} onChange={(e) => patchAnswer(a.id, { criteria: a.criteria.map((x) => (x.id === c.id ? { ...x, correct: e.target.value as Verdict } : x)) })}
                  className="px-2 py-1 rounded border text-xs" style={inputStyle}>
                  {VERDICTS.map((v) => <option key={v} value={v}>{VERDICT_LABEL[v]}</option>)}
                </select>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
